import * as wells from "../views/maps/layers/wells";
import * as landtracUnits from "../views/maps/layers/landtracUnits";
import * as landtracLeases from "../views/maps/layers/landtracLeases";
import * as units from "../views/maps/layers/units";
import * as unitEvaluations from "../views/maps/layers/unitEvaluations";
import * as assets from "../views/maps/layers/assets";
import * as permitPlats from "../views/maps/layers/permitPlats";
import * as tracts from "../views/maps/layers/tracts";
import * as areaOfInterests from "../views/maps/layers/areaOfInterests";
import * as rigs from "../views/maps/layers/rigs";
import * as enverusLeases from "../views/maps/layers/enverusLeases";
import * as plays from "../views/maps/layers/plays";
import * as basins from "../views/maps/layers/basins";
import * as faults from "../views/maps/layers/faults";
import * as landGrid from "../views/maps/layers/landGrid";

import * as layers from "../views/maps/layers";
import {
  loadCoordinateSystem,
  saveCoordinateSystem,
} from "../views/maps/common/coordinates";
import syncDrawState from "../views/maps/controls/toolCustomization/syncDrawState";

export const types = {
  MAP_RESET: "MAP_RESET",
  MAP_RESET_VIEW: "MAP_RESET_VIEW",
  // Controls
  MAP_CONTROLS_SET_DRAW: "MAP_CONTROLS_SET_DRAW",
  MAP_CONTROLS_SET_SAVED_GEOMETRY: "MAP_CONTROLS_SET_SAVED_GEOMETRY",
  MAP_CONTROLS_SET_PAGE_ENTITY: "MAP_CONTROLS_SET_PAGE_ENTITY",
  MAP_CONTROLS_SET_PAGE_CONTEXT: "MAP_CONTROLS_SET_PAGE_CONTEXT",
  MAP_CONTROLS_SET_PAGE_DELETED_RECORD_IDS:
    "MAP_CONTROLS_SET_PAGE_DELETED_RECORD_IDS",
  MAP_CONTROLS_PASTE_GEOMETRY_AND_EDIT: "MAP_CONTROLS_PASTE_GEOMETRY_AND_EDIT",
  MAP_CONTROLS_REQUEST_GEOM_EDIT_SAVE: "MAP_CONTROLS_REQUEST_GEOM_EDIT_SAVE",
  MAP_CONTROLS_PUSH_FILTER_SNAPSHOT: "MAP_CONTROLS_PUSH_FILTER_SNAPSHOT",
  MAP_CONTROLS_CLEAR_FILTER_SNAPSHOTS: "MAP_CONTROLS_CLEAR_FILTER_SNAPSHOTS",
  MAP_CONTROLS_GEOM_SAVED: "MAP_CONTROLS_GEOM_SAVED",
  MAP_CONTROLS_SET_COORDINATE_SYSTEM: "MAP_CONTROLS_SET_COORDINATE_SYSTEM",
  MAP_CONTROLS_SET_COORDINATES_ACTIVE: "MAP_CONTROLS_SET_COORDINATES_ACTIVE",
  // Layers
  MAP_SET_ZOOM_TO_FILTER: "MAP_SET_ZOOM_TO_FILTER",
  MAP_LAYER_RESET_FILTERS: "MAP_LAYER_RESET_FILTERS",
  MAP_LAYER_SET_VISIBILITY: "MAP_LAYER_SET_VISIBILITY",
  MAP_LAYER_SET_FILTERS_VISIBILITY: "MAP_LAYER_SET_FILTERS_VISIBILITY",
  MAP_LAYER_SET_FILTER_COLUMN_OPEN: "MAP_LAYER_SET_FILTER_COLUMN_OPEN",
  MAP_LAYER_SET_FILTER_COLUMN_VALUE: "MAP_LAYER_SET_FILTER_COLUMN_VALUE",
  // Styles
  MAP_LAYER_SET_ANNOTATION_VISIBILITY: "MAP_LAYER_SET_ANNOTATION_VISIBILITY",
  MAP_LAYER_SET_CLICKABLE: "MAP_LAYER_SET_CLICKABLE",
  // Table
  MAP_SET_TABLE_HOVER_RECORD: "MAP_SET_TABLE_HOVER_RECORD",
};

export const resetMap = (mapRef) => ({
  type: types.MAP_RESET,
  mapRef,
});

// Reset only the map "view" (layers/filters/savedGeometry); preserve the
// current draw/edit session and page context. Use this in entity *.Map
// pages where setMapFilters runs on entity change.
export const resetMapView = (mapRef) => ({
  type: types.MAP_RESET_VIEW,
  mapRef,
});

export const setDraw = (drawRef, drawOptions) => ({
  type: types.MAP_CONTROLS_SET_DRAW,
  drawRef,
  value: drawOptions,
});

export const setMultipleLines = (drawRef, patch) => (dispatch, getState) => {
  const multipleLines = {
    ...getState().map.controls.draw.multipleLines,
    ...patch,
  };

  dispatch(setDraw(drawRef, { multipleLines }));
};

export const setSavedGeometry = (value) => ({
  type: types.MAP_CONTROLS_SET_SAVED_GEOMETRY,
  value,
});

export const setCoordinateSystem = (value) => (dispatch, getState) => {
  const coordinateSystem = {
    ...getState().map.controls.coordinateSystem,
    ...value,
  };

  saveCoordinateSystem(coordinateSystem);

  dispatch({
    type: types.MAP_CONTROLS_SET_COORDINATE_SYSTEM,
    value: coordinateSystem,
  });
};

export const setCoordinatesActive = (value) => ({
  type: types.MAP_CONTROLS_SET_COORDINATES_ACTIVE,
  value,
});

export const setPageEntity = (entity) => ({
  type: types.MAP_CONTROLS_SET_PAGE_ENTITY,
  value: entity,
});

// Free-form record context for the surrounding entity page (e.g. the
// current Permit on PermitMap), so popover forms can prefill from it.
// Toolbar lives in a separate React root, so context propagation has
// to go through Redux rather than React Context.
export const setPageContext = (context) => ({
  type: types.MAP_CONTROLS_SET_PAGE_CONTEXT,
  value: context,
});

// Ids of soft-deleted records the page wants kept visible; the consuming
// domain interprets them (wells maps pass well ids so tiles / polygon
// analysis still surface those deleted wells). Set by useMap.
export const setPageDeletedRecordIds = (value) => ({
  type: types.MAP_CONTROLS_SET_PAGE_DELETED_RECORD_IDS,
  value,
});

export const pasteGeometryAndEdit = ({
  geometry,
  editTarget,
  initialValues,
}) => ({
  type: types.MAP_CONTROLS_PASTE_GEOMETRY_AND_EDIT,
  value: { geometry, editTarget, initialValues },
});

// `values` carries the edit form's fields (e.g. operator) alongside the
// geometry — null when the handler has no form.
export const requestGeomEditSave = (values = null) => ({
  type: types.MAP_CONTROLS_REQUEST_GEOM_EDIT_SAVE,
  value: values,
});

// Snapshot a layer filter value before a tool overrides it (e.g. polygon
// analysis "get wells in polygon"). First push for a given (layerId, column)
// wins until cleared on Save/Cancel.
export const pushFilterSnapshot = ({ layerId, column, value }) => ({
  type: types.MAP_CONTROLS_PUSH_FILTER_SNAPSHOT,
  value: { layerId, column, value },
});

export const clearFilterSnapshots = () => ({
  type: types.MAP_CONTROLS_CLEAR_FILTER_SNAPSHOTS,
});

// Broadcast that a geom edit has been saved. Pages observe this via
// useEntityRefetchOnSave to refresh their displayed entity.
export const geomSaveCompleted = ({ layerId, id }) => ({
  type: types.MAP_CONTROLS_GEOM_SAVED,
  value: { layerId, id },
});

export const setZoomToFilter = (value) => ({
  type: types.MAP_SET_ZOOM_TO_FILTER,
  value,
});

export const setLayerFiltersVisibility = (layerId, value) => ({
  type: types.MAP_LAYER_SET_FILTERS_VISIBILITY,
  layerId,
  value,
});

export const setLayerFilterColumnOpen = (layerId, column, value) => ({
  type: types.MAP_LAYER_SET_FILTER_COLUMN_OPEN,
  layerId,
  column,
  value,
});

export const setLayerAnnotationVisibility = (mapRef, layerId, value) => ({
  type: types.MAP_LAYER_SET_ANNOTATION_VISIBILITY,
  mapRef,
  layerId,
  value,
});

export const setLayerClickable = (layerId, value) => (dispatch) => {
  layers.saveLayerClickable(layerId, value);

  dispatch({
    type: types.MAP_LAYER_SET_CLICKABLE,
    layerId,
    value,
  });
};

export const setLayerVisibility = (mapRef, layerId, value) => ({
  type: types.MAP_LAYER_SET_VISIBILITY,
  mapRef,
  layerId,
  value,
});

export const setLayerFilterColumnValue = (
  mapRef,
  layerId,
  column,
  value,
  dtype = "string",
  excludeSelected = false,
  excludeNullValues = false
) => ({
  type: types.MAP_LAYER_SET_FILTER_COLUMN_VALUE,
  mapRef,
  layerId,
  column,
  value,
  dtype,
  excludeSelected,
  excludeNullValues,
});

export const resetLayerFilters = (mapRef, layerId) => ({
  type: types.MAP_LAYER_RESET_FILTERS,
  mapRef,
  layerId,
});

export const setTableHoverRecord = (value) => ({
  type: types.MAP_SET_TABLE_HOVER_RECORD,
  value,
});

const baseInitialState = {
  loading: false,
  zoomToFilter: true,
  controls: {
    draw: {
      edit: false,
      mode: null,
      snap: false,
      // Zero until the user types one or an area of interest supplies one.
      multipleLines: { spacingFt: 0 },
      saveRequestToken: 0,
      editTarget: null,
      pendingPasteGeometry: null,
      pendingInitialValues: null,
      pendingSaveValues: null,
    },
    page: {
      entity: null, // { layerId, id } — set by useDeclareEditableEntity
      context: null, // arbitrary record (e.g. { permit }) — set by useDeclareEditingContext
      deletedRecordIds: [], // soft-deleted record ids to keep visible — set by useMap
    },
    savedGeometry: null,
    filterSnapshots: [],
    lastSaved: null, // { layerId, id, token } — incremented on each save
    coordinateSystem: loadCoordinateSystem(), // { datum, format }
    coordinatesActive: false,
  },
  tableHoverRecord: null,
  styles: {
    [wells.LAYER_ID]: {
      annotationVisible: true,
    },
    [faults.LAYER_ID]: {
      annotationVisible: false,
    },
    [plays.LAYER_ID]: {
      annotationVisible: false,
    },
    [basins.LAYER_ID]: {
      annotationVisible: false,
    },
    [landGrid.LAYER_ID]: {
      annotationVisible: true,
      clickable: layers.loadLayerClickable(landGrid.LAYER_ID),
    },
  },
  layers: {
    [wells.LAYER_ID]: {
      layerVisible: true,
      filtersVisible: false,
      filterColumnOpen: null,
      filters: wells.INITIAL_FILTER_STATE,
    },
    [landtracUnits.LAYER_ID]: {
      layerVisible: false,
      filtersVisible: false,
      filterColumnOpen: null,
      filters: landtracUnits.INITIAL_FILTER_STATE,
    },
    [landtracLeases.LAYER_ID]: {
      layerVisible: false,
      filtersVisible: false,
      filterColumnOpen: null,
      filters: landtracLeases.INITIAL_FILTER_STATE,
    },
    [units.LAYER_ID]: {
      layerVisible: false,
      filtersVisible: false,
      filterColumnOpen: null,
      filters: units.INITIAL_FILTER_STATE,
    },
    [unitEvaluations.LAYER_ID]: {
      layerVisible: false,
      filtersVisible: false,
      filterColumnOpen: null,
      filters: unitEvaluations.INITIAL_FILTER_STATE,
    },
    [assets.LAYER_ID]: {
      layerVisible: false,
      filtersVisible: false,
      filterColumnOpen: null,
      filters: assets.INITIAL_FILTER_STATE,
    },
    [permitPlats.LAYER_ID]: {
      layerVisible: false,
      filtersVisible: false,
      filterColumnOpen: null,
      filters: permitPlats.INITIAL_FILTER_STATE,
    },
    [tracts.LAYER_ID]: {
      layerVisible: false,
      filtersVisible: false,
      filterColumnOpen: null,
      filters: tracts.INITIAL_FILTER_STATE,
    },
    [areaOfInterests.LAYER_ID]: {
      layerVisible: false,
      filtersVisible: false,
      filterColumnOpen: null,
      filters: areaOfInterests.INITIAL_FILTER_STATE,
    },
    [rigs.LAYER_ID]: {
      layerVisible: true,
      filtersVisible: false,
      filterColumnOpen: null,
      filters: rigs.INITIAL_FILTER_STATE,
    },
    [enverusLeases.LAYER_ID]: {
      layerVisible: false,
      filtersVisible: false,
      filterColumnOpen: null,
      filters: enverusLeases.INITIAL_FILTER_STATE,
    },
  },
};

const applyFilterPresets = (state) => ({
  ...state,
  layers: Object.fromEntries(
    Object.entries(state.layers).map(([layerId, layer]) => {
      if (!layer.layerVisible) return [layerId, layer];

      const filters = Object.fromEntries(
        Object.entries(layer.filters).map(([column, filter]) => [
          column,
          "presetValue" in filter
            ? {
                ...filter,
                value: filter.presetValue,
                excludeSelected:
                  filter.presetExcludeSelected ?? filter.excludeSelected,
              }
            : filter,
        ])
      );

      return [layerId, { ...layer, filters }];
    })
  ),
});

export const applyLayerFilterPresets =
  (mapRef, layerId) => (dispatch, getState) => {
    const filters = getState().map.layers[layerId]?.filters || {};

    Object.values(filters).forEach((filter) => {
      if (!("presetValue" in filter)) return;

      dispatch(
        setLayerFilterColumnValue(
          mapRef,
          layerId,
          filter.column,
          filter.presetValue,
          filter.dtype,
          filter.presetExcludeSelected ?? filter.excludeSelected,
          filter.excludeNullValues
        )
      );
    });
  };

export const initialState = applyFilterPresets(baseInitialState);

/* Default styles, keeping each layer's current clickable choice. */
const resetStyles = (state) =>
  Object.fromEntries(
    Object.entries(initialState.styles).map(([layerId, layerStyle]) => [
      layerId,
      "clickable" in layerStyle
        ? { ...layerStyle, clickable: state.styles[layerId].clickable }
        : layerStyle,
    ])
  );

export function reducer(state = initialState, action) {
  const { type, layerId, value, excludeSelected, excludeNullValues } = action;

  switch (type) {
    case types.MAP_RESET:
      layers.resetMap(action, initialState);
      return {
        ...initialState,
        controls: {
          ...initialState.controls,
          coordinateSystem: state.controls.coordinateSystem,
        },
        styles: resetStyles(state),
      };
    case types.MAP_RESET_VIEW:
      // Resets layers/visibility/filter values to defaults but keeps the
      // current edit session and page context.
      layers.resetMap(action, initialState);
      return {
        ...initialState,
        controls: {
          ...initialState.controls,
          draw: state.controls.draw,
          page: state.controls.page,
          filterSnapshots: state.controls.filterSnapshots,
          lastSaved: state.controls.lastSaved,
          coordinateSystem: state.controls.coordinateSystem,
        },
        styles: resetStyles(state),
      };
    case types.MAP_CONTROLS_SET_DRAW: {
      const draw = { ...state.controls.draw, ...value };

      syncDrawState({
        ...action,
        value: { ...value, multipleLines: draw.multipleLines },
      });

      return {
        ...state,
        controls: {
          ...state.controls,
          draw,
          coordinatesActive:
            value.edit === false ? false : state.controls.coordinatesActive,
        },
      };
    }
    case types.MAP_CONTROLS_SET_SAVED_GEOMETRY:
      return {
        ...state,
        controls: {
          ...state.controls,
          savedGeometry: value,
        },
      };
    case types.MAP_CONTROLS_SET_COORDINATES_ACTIVE:
      return {
        ...state,
        controls: {
          ...state.controls,
          coordinatesActive: value,
        },
      };
    case types.MAP_CONTROLS_SET_COORDINATE_SYSTEM:
      return {
        ...state,
        controls: {
          ...state.controls,
          coordinateSystem: value,
        },
      };
    case types.MAP_CONTROLS_SET_PAGE_ENTITY:
      return {
        ...state,
        controls: {
          ...state.controls,
          page: {
            ...state.controls.page,
            entity: value,
          },
        },
      };
    case types.MAP_CONTROLS_SET_PAGE_CONTEXT:
      return {
        ...state,
        controls: {
          ...state.controls,
          page: {
            ...state.controls.page,
            context: value,
          },
        },
      };
    case types.MAP_CONTROLS_SET_PAGE_DELETED_RECORD_IDS:
      return {
        ...state,
        controls: {
          ...state.controls,
          page: {
            ...state.controls.page,
            deletedRecordIds: value,
          },
        },
      };
    case types.MAP_CONTROLS_PASTE_GEOMETRY_AND_EDIT:
      return {
        ...state,
        controls: {
          ...state.controls,
          draw: {
            ...state.controls.draw,
            edit: true,
            editTarget: value.editTarget,
            pendingPasteGeometry: value.geometry,
            pendingInitialValues: value.initialValues || null,
            pendingSaveValues: null,
          },
        },
      };
    case types.MAP_CONTROLS_REQUEST_GEOM_EDIT_SAVE:
      return {
        ...state,
        controls: {
          ...state.controls,
          draw: {
            ...state.controls.draw,
            saveRequestToken: state.controls.draw.saveRequestToken + 1,
            pendingSaveValues: value || null,
          },
        },
      };
    case types.MAP_CONTROLS_PUSH_FILTER_SNAPSHOT: {
      // First push for a (layerId, column) wins until cleared.
      const exists = state.controls.filterSnapshots.some(
        (s) => s.layerId === value.layerId && s.column === value.column
      );
      if (exists) return state;
      return {
        ...state,
        controls: {
          ...state.controls,
          filterSnapshots: [...state.controls.filterSnapshots, value],
        },
      };
    }
    case types.MAP_CONTROLS_CLEAR_FILTER_SNAPSHOTS:
      if (state.controls.filterSnapshots.length === 0) return state;
      return {
        ...state,
        controls: {
          ...state.controls,
          filterSnapshots: [],
        },
      };
    case types.MAP_CONTROLS_GEOM_SAVED:
      return {
        ...state,
        controls: {
          ...state.controls,
          lastSaved: {
            layerId: value.layerId,
            id: value.id,
            token: (state.controls.lastSaved?.token || 0) + 1,
          },
        },
      };
    case types.MAP_SET_ZOOM_TO_FILTER:
      return {
        ...state,
        zoomToFilter: value,
      };
    case types.MAP_LAYER_SET_FILTERS_VISIBILITY:
      return {
        ...state,
        layers: {
          ...state.layers,
          [layerId]: {
            ...state.layers[layerId],
            filtersVisible: value,
          },
        },
      };
    case types.MAP_LAYER_SET_FILTER_COLUMN_OPEN:
      return {
        ...state,
        layers: {
          ...state.layers,
          [layerId]: {
            ...state.layers[layerId],
            filterColumnOpen: value,
          },
        },
      };
    case types.MAP_LAYER_SET_ANNOTATION_VISIBILITY:
      layers.setAnnotationVisibility(action);
      return {
        ...state,
        styles: {
          ...state.styles,
          [layerId]: {
            ...state.styles[layerId],
            annotationVisible: value,
          },
        },
      };
    case types.MAP_LAYER_SET_CLICKABLE:
      return {
        ...state,
        styles: {
          ...state.styles,
          [layerId]: {
            ...state.styles[layerId],
            clickable: value,
          },
        },
      };
    case types.MAP_LAYER_SET_VISIBILITY:
      layers.setLayerVisibility(action, state.styles);
      return {
        ...state,
        layers: {
          ...state.layers,
          [layerId]: {
            ...state.layers[layerId],
            layerVisible: value,
          },
        },
      };
    case types.MAP_LAYER_SET_FILTER_COLUMN_VALUE: {
      const nextFilters = {
        ...state.layers[layerId].filters,
        [action.column]: {
          ...state.layers[layerId].filters[action.column],
          value: value,
          excludeSelected: excludeSelected,
          excludeNullValues: excludeNullValues,
        },
      };
      const combinedExpression =
        layers.buildCombinedFilterExpression(nextFilters);

      layers.setLayerFilterExpression({
        mapRef: action.mapRef,
        layerId,
        filterExpression: combinedExpression,
      });

      return {
        ...state,
        layers: {
          ...state.layers,
          [layerId]: {
            ...state.layers[layerId],
            filters: nextFilters,
          },
        },
      };
    }
    case types.MAP_LAYER_RESET_FILTERS:
      layers.setLayerFilterExpression(action);
      return {
        ...state,
        layers: {
          ...state.layers,
          [layerId]: {
            ...state.layers[layerId],
            filterColumnOpen: null,
            filters: baseInitialState.layers[layerId].filters,
          },
        },
      };
    case types.MAP_SET_TABLE_HOVER_RECORD:
      return {
        ...state,
        tableHoverRecord: value,
      };
    default:
      return state;
  }
}

const actions = {
  resetMap,
  resetMapView,
  // Controls
  setDraw,
  setMultipleLines,
  setSavedGeometry,
  setCoordinateSystem,
  setCoordinatesActive,
  setPageEntity,
  setPageContext,
  setPageDeletedRecordIds,
  pasteGeometryAndEdit,
  requestGeomEditSave,
  pushFilterSnapshot,
  clearFilterSnapshots,
  geomSaveCompleted,
  // Layers
  setZoomToFilter,
  resetLayerFilters,
  applyLayerFilterPresets,
  setLayerVisibility,
  setLayerFiltersVisibility,
  setLayerFilterColumnValue,
  setLayerFilterColumnOpen,
  // Styles
  setLayerAnnotationVisibility,
  setLayerClickable,
  // Table
  setTableHoverRecord,
};

export default actions;
